/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Card, Dialog, Divider, Link, Typography2 } from '@coursera/cds-core';
import { EmailIcon, HelpIcon, UploadIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useEnterpriseAdminTrackingData } from 'bundles/enterprise-admin-hooks/hooks/useEnterpriseAdminTrackingData';
import type {
  InviteEntryPoint,
  InviteMethod,
} from 'bundles/enterprise-admin-learners/components/invite-learners/types';
import { INVITE_LEARN_MORE_URL } from 'bundles/enterprise-admin-learners/constants';
import { LEARNERS_BUTTON } from 'bundles/enterprise-admin-learners/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content } = Dialog;

const styles = {
  optionStack: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-100);
  `,
  card: css`
    cursor: pointer;

    &:hover,
    &:focus {
      background: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  cardContent: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    padding: var(--cds-spacing-100);
  `,
  iconTile: css`
    flex-shrink: 0;
    padding: var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-100);
    background: var(--cds-color-emphasis-primary-background-xweak);
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  cardText: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  domainRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--cds-spacing-200);
  `,
  domainText: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  footer: css`
    padding-top: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-300);
  `,
  learnMore: css`
    &&,
    &&:hover,
    &&:focus {
      color: var(--cds-color-neutral-primary);
      text-decoration: underline;
    }

    &&&:visited {
      color: var(--cds-color-neutral-primary);
    }

    && svg {
      color: var(--cds-color-neutral-primary);
    }
  `,
};

export type InviteMethodModalProps = {
  open: boolean;
  onClose: () => void;
  entryPoint: InviteEntryPoint;
  onSelectMethod: (method: InviteMethod) => void;
  /** Chat message that launched the modal from an Admin Coach action. */
  coachMessageId?: string;
};

// Card renders a div, not a native button — without this, keyboard users can't
// activate the email/CSV options (WCAG 2.1.1).
const handleActivationKey = (onActivate: () => void) => (event: React.KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onActivate();
  }
};

const getInviteCopy = () => ({
  title: _t('Choose how to invite learners'),
  email: {
    title: _t('Invite via email'),
    description: _t('Add names and emails manually for up to 10 learners.'),
  },
  csv: {
    title: _t('Invite in bulk'),
    description: _t('For larger lists, upload a CSV and include additional learner details.'),
  },
  domain: {
    title: _t('Invite via adding a domain'),
    description: _t('Add a domain to allow anyone with a matching email to join Coursera.'),
  },
});

const getAddCopy = () => ({
  title: _t('Choose how to add learners'),
  email: {
    title: _t('Add via email'),
    description: _t('Add names and emails manually for up to 10 learners.'),
  },
  csv: {
    title: _t('Add in bulk'),
    description: _t('For larger lists, upload a CSV and include additional learner details.'),
  },
  domain: {
    title: _t('Add via adding a domain'),
    description: _t('Add a domain to allow anyone with a matching email to join Coursera.'),
  },
});

// Home, All Learners, and Admin Coach currently share "Invite" copy; Group Details
// gets "Add" copy since learners are being added to an existing group, not invited to the org.
const COPY_BUILDERS: Record<InviteEntryPoint, () => ReturnType<typeof getInviteCopy>> = {
  home: getInviteCopy,
  allLearners: getInviteCopy,
  groupDetails: getAddCopy,
  adminCoach: getInviteCopy,
};

const getCopy = (entryPoint: InviteEntryPoint) => COPY_BUILDERS[entryPoint]();

/**
 * Step 1 of the invite flow: choose email, CSV, or org-level domain allowlisting.
 * Selecting a method hands off to `InviteLearnersModal`'s step state — this component
 * has no state of its own.
 */
const METHOD_BUTTON_NAME: Record<InviteMethod, ButtonName> = {
  email: LEARNERS_BUTTON.INVITE_VIA_EMAIL,
  csv: LEARNERS_BUTTON.INVITE_VIA_CSV,
  domain: LEARNERS_BUTTON.INVITE_VIA_DOMAIN,
};

const InviteMethodModal = ({ open, onClose, entryPoint, onSelectMethod, coachMessageId }: InviteMethodModalProps) => {
  const copy = getCopy(entryPoint);
  const track = useTracker();
  const { enterprise } = useEnterpriseAdminTrackingData();

  const handleSelectMethod = (method: InviteMethod) => {
    track('click_button', {
      button: { name: METHOD_BUTTON_NAME[method] },
      enterprise,
      ...(coachMessageId ? { coach: { chatMessageId: coachMessageId } } : {}),
    });
    onSelectMethod(method);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <HeadingGroup>{copy.title}</HeadingGroup>
      <Content>
        <div css={styles.optionStack}>
          <Card
            css={styles.card}
            role="button"
            tabIndex={0}
            aria-label={copy.email.title}
            onClick={() => handleSelectMethod('email')}
            onKeyDown={handleActivationKey(() => handleSelectMethod('email'))}
          >
            <div css={styles.cardContent}>
              <div css={styles.iconTile}>
                <EmailIcon size="large" color="interactive" />
              </div>
              <div css={styles.cardText}>
                <Typography2 component="h3" variant="subtitleMedium">
                  {copy.email.title}
                </Typography2>
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {copy.email.description}
                </Typography2>
              </div>
            </div>
          </Card>
          <Card
            css={styles.card}
            role="button"
            tabIndex={0}
            aria-label={copy.csv.title}
            onClick={() => handleSelectMethod('csv')}
            onKeyDown={handleActivationKey(() => handleSelectMethod('csv'))}
          >
            <div css={styles.cardContent}>
              <div css={styles.iconTile}>
                <UploadIcon size="large" color="interactive" />
              </div>
              <div css={styles.cardText}>
                <Typography2 component="h3" variant="subtitleMedium">
                  {copy.csv.title}
                </Typography2>
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {copy.csv.description}
                </Typography2>
              </div>
            </div>
          </Card>
          <Divider color="light" />
          <div css={styles.domainRow}>
            <div css={styles.domainText}>
              <Typography2 component="h3" variant="subtitleMedium">
                {copy.domain.title}
              </Typography2>
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {copy.domain.description}
              </Typography2>
            </div>
            <Button variant="secondary" onClick={() => handleSelectMethod('domain')}>
              {_t('Set up')}
            </Button>
          </div>
        </div>
        <div css={styles.footer}>
          <Link
            href={INVITE_LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            icon={<HelpIcon size="small" color="default" />}
            iconPosition="before"
            typographyVariant="bodySecondary"
            css={styles.learnMore}
          >
            {_t('Learn more about invitation')}
          </Link>
        </div>
      </Content>
    </Dialog>
  );
};

export default InviteMethodModal;
